import React, { createContext, useContext, useEffect, useState } from 'react'

interface AnimationSettings {
  enabled: boolean
  intensity: 'low' | 'medium' | 'high'
  starfieldSpeed: number
  particleCount: number
  glowEffects: boolean
  transitions: boolean
  floatingElements: boolean
  backgroundAnimations: boolean
  reduceMotion: boolean
  preset: 'performance' | 'balanced' | 'cinematic' | 'minimal' | 'snow'
  animationSpeed: number
}

interface AnimationContextType {
  settings: AnimationSettings
  updateSettings: (newSettings: Partial<AnimationSettings>) => void
  toggleAnimations: () => void
  presets: {
    performance: AnimationSettings
    balanced: AnimationSettings
    cinematic: AnimationSettings
    minimal: AnimationSettings
    snow: AnimationSettings
  }
}

const defaultSettings: AnimationSettings = {
  enabled: true,
  intensity: 'high',
  starfieldSpeed: 1.2,
  particleCount: 500,
  glowEffects: true,
  transitions: true,
  floatingElements: true,
  backgroundAnimations: true,
  reduceMotion: false,
  preset: 'cinematic',
  animationSpeed: 1
}

const presets = {
  performance: {
    enabled: true,
    intensity: 'low' as const,
    starfieldSpeed: 0.5,
    particleCount: 150,
    glowEffects: false,
    transitions: true,
    floatingElements: false,
    backgroundAnimations: false,
    reduceMotion: false,
    preset: 'performance' as const,
    animationSpeed: 0.8
  },
  balanced: {
    enabled: true,
    intensity: 'medium' as const,
    starfieldSpeed: 0.8,
    particleCount: 300,
    glowEffects: true,
    transitions: true,
    floatingElements: true,
    backgroundAnimations: false,
    reduceMotion: false,
    preset: 'balanced' as const,
    animationSpeed: 1
  },
  cinematic: {
    enabled: true,
    intensity: 'high' as const,
    starfieldSpeed: 1.5,
    particleCount: 800,
    glowEffects: true,
    transitions: true,
    floatingElements: true,
    backgroundAnimations: true,
    reduceMotion: false,
    preset: 'cinematic' as const,
    animationSpeed: 1.2
  },
  minimal: {
    enabled: false,
    intensity: 'low' as const,
    starfieldSpeed: 0,
    particleCount: 0,
    glowEffects: false,
    transitions: false,
    floatingElements: false,
    backgroundAnimations: false,
    reduceMotion: true,
    preset: 'minimal' as const,
    animationSpeed: 0.5
  },
  snow: {
    enabled: true,
    intensity: 'medium' as const,
    starfieldSpeed: 0.3,
    particleCount: 400,
    glowEffects: false,
    transitions: true,
    floatingElements: false,
    backgroundAnimations: true,
    reduceMotion: false,
    preset: 'snow' as const,
    animationSpeed: 0.6
  }
}

const AnimationContext = createContext<AnimationContextType | undefined>(undefined)

export const useAnimations = () => {
  const context = useContext(AnimationContext)
  if (!context) {
    throw new Error('useAnimations must be used within an AnimationProvider')
  }
  return context
}

interface AnimationProviderProps {
  children: React.ReactNode
}

export const AnimationProvider: React.FC<AnimationProviderProps> = ({ children }) => {
  // Load settings from localStorage or use defaults
  const [settings, setSettings] = useState<AnimationSettings>(() => {
    try {
      const saved = localStorage.getItem('crown-of-suns-animations')
      if (saved) {
        return { ...defaultSettings, ...JSON.parse(saved) }
      }
    } catch (error) {
      console.warn('Failed to load animation settings:', error)
    }
    
    // Check for user's reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      return { ...defaultSettings, ...presets.minimal }
    }
    
    return defaultSettings
  })

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('crown-of-suns-animations', JSON.stringify(settings))
    } catch (error) {
      console.warn('Failed to save animation settings:', error)
    }
  }, [settings])

  // Listen for system reduced motion changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    
    const handleChange = (e: MediaQueryListEvent) => {
      setSettings(prev => ({
        ...prev,
        reduceMotion: e.matches
      }))
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Apply CSS custom properties for animations
  useEffect(() => {
    const root = document.documentElement
    
    if (!settings.enabled || settings.reduceMotion) {
      root.style.setProperty('--animation-duration', '0s')
      root.style.setProperty('--transition-duration', '0s')
      root.style.setProperty('--animation-play-state', 'paused')
    } else {
      const duration = settings.intensity === 'high' ? '1s' : 
                      settings.intensity === 'medium' ? '1.5s' : '2s'
      const transitionDuration = settings.transitions ? '0.3s' : '0s'
      
      root.style.setProperty('--animation-duration', duration)
      root.style.setProperty('--transition-duration', transitionDuration)
      root.style.setProperty('--animation-play-state', 'running')
    }
    
    // Set glow effect variables
    root.style.setProperty('--glow-enabled', settings.glowEffects ? '1' : '0')
    root.style.setProperty('--float-enabled', settings.floatingElements ? '1' : '0')
  }, [settings])

  const updateSettings = (newSettings: Partial<AnimationSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }))
  }

  const toggleAnimations = () => {
    setSettings(prev => ({ ...prev, enabled: !prev.enabled }))
  }

  const contextValue: AnimationContextType = {
    settings,
    updateSettings,
    toggleAnimations,
    presets
  }

  return (
    <AnimationContext.Provider value={contextValue}>
      {children}
    </AnimationContext.Provider>
  )
}

// Enhanced animation utility hooks
export const useAnimationClass = (baseClass: string, animatedClass: string) => {
  const { settings } = useAnimations()
  
  if (!settings.enabled || settings.reduceMotion) {
    return baseClass
  }
  
  return `${baseClass} ${animatedClass}`
}

export const useTransitionClass = (transitionClass: string) => {
  const { settings } = useAnimations()
  
  if (!settings.transitions || settings.reduceMotion) {
    return ''
  }
  
  return transitionClass
}

export const useFloatingAnimation = () => {
  const { settings } = useAnimations()
  
  return settings.floatingElements && settings.enabled && !settings.reduceMotion
}

export const useGlowEffect = () => {
  const { settings } = useAnimations()
  
  return settings.glowEffects && settings.enabled && !settings.reduceMotion
}